import React, { Suspense, lazy } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import Spinner from './components/Spinner';
import ChunkLoadErrorBoundary from './components/ChunkLoadErrorBoundary';
import PrivateRoute from './components/PrivateRoute';
import SignInPage from './components/SignIn';
import { useAuthContext } from './context';
import { isBatEmployee } from './utils/roles';
const Home = lazy(() => import('./views/Home'));
const Notifications = lazy(() => import('./notifications/Page'));

const Product = lazy(() => import('./views/Products'));
const Customer = lazy(() => import('./views/Customers'));
const Cart = lazy(() => import('./components/Cart'));
const Order = lazy(() => import('./components/Orders'));
const PendingOrder = lazy(() => import('./views/PendingOrder'));
const Account = lazy(() => import('./components/Account'));
const Admin = lazy(() => import('./views/Admin'));
const Reports = lazy(() => import('./views/Reports'));
const OcrPreview = lazy(() => import('./views/OcrPreview'));
const SalesmanMap = lazy(() => import('./views/Reports/SalesmanMapReport'));
const SalesmanReport = lazy(() => import('./views/Reports/SalesmanReport'));
const SalesHistoryReport = lazy(() =>
  import('./views/Reports/SalesHistoryReport'),
);
const KoOrderReport = lazy(() => import('./views/Reports/KoOrderReport'));
const PreCache = lazy(() => import('./views/ImageCache'));
const CartDrafts = lazy(() => import('./components/CartDrafts'));
const Tasks = lazy(() => import('./views/Tasksnew'));
const CreateTask = lazy(() => import('./views/CreateTask'));
const TaskDetails = lazy(() => import('./views/TaskDetails'));
const TaskBatchDocs = lazy(() => import('./views/TaskBatchDocs'));
const TaskManage = lazy(() => import('./views/TaskManage'));
const CustomerActionPage = lazy(() => import('./views/CustomerActions'));
const NewCustomer = lazy(() => import('./views/NewCustomer'));
const DeviceApproval = lazy(() => import('./views/DeviceApproval'));
const BatSurvey = lazy(() => import('./views/BatSurvey'));

const BAT_ALLOWED_PATHS = ['/home', '/bat-survey', '/device-approval'];

const Routes = () => {
  const location = useLocation();
  const { authUser, deviceRegistryStatus } = useAuthContext();
  const registryResult = deviceRegistryStatus?.results || deviceRegistryStatus;
  const requiresDeviceApproval = authUser && registryResult?.accessAllowed === false;
  const isDeviceApprovalRoute = location.pathname === '/device-approval';
  const isSignInRoute =
    location.pathname === '/sign-in' || location.pathname === '/index.html';
  const isBatRestrictedRoute =
    isBatEmployee(authUser) &&
    !isSignInRoute &&
    !BAT_ALLOWED_PATHS.includes(location.pathname) &&
    !/^\/notifications(?:\/[^/]+)?$/.test(location.pathname);

  return (
    <ChunkLoadErrorBoundary>
      <Suspense fallback={<Spinner />}>
        <Switch>
          <Route exact path="/sign-in" component={SignInPage} />
          <Route exact path="/index.html" component={SignInPage} />
          {requiresDeviceApproval &&
            !isDeviceApprovalRoute &&
            !isSignInRoute && <Redirect to="/device-approval" />}
          {isBatRestrictedRoute && <Redirect to="/home" />}
          {authUser && !isBatEmployee(authUser) &&
            location.pathname === '/bat-survey' && <Redirect to="/home" />}
          <PrivateRoute exact path="/device-approval" component={DeviceApproval} />
          <PrivateRoute exact path="/notifications/:id?" component={Notifications} />
          <PrivateRoute exact path="/bat-survey" component={BatSurvey} />
          <Route exact path="/home" component={Home} />
          <Route exact path="/customers" component={Customer} />
          <Route exact path="/customer-action" component={CustomerActionPage} />
          <Route exact path="/products" component={Product} />
          <PrivateRoute exact path="/cart" component={Cart} />
          <PrivateRoute path="/tasks" component={Tasks} />
          <PrivateRoute path="/task/:id" component={TaskDetails} />
          <PrivateRoute path="/task-batch-docs" component={TaskBatchDocs} />
          <PrivateRoute path="/task-manage/:taskId" component={TaskManage} />
          <PrivateRoute path="/create-task" component={CreateTask} />
          <PrivateRoute exact path="/new-customer" component={NewCustomer} />
          <Route exact path="/drafts" component={CartDrafts} />
          <PrivateRoute exact path="/reports/salesmans/report" component={SalesmanReport} />
          <PrivateRoute exact path="/reports/sales-history" component={SalesHistoryReport} />
          <PrivateRoute exact path="/reports/ko-orders" component={KoOrderReport} />
          <PrivateRoute exact path="/imageCache" component={PreCache} />
          <PrivateRoute exact path="/reports/salesmans/map" component={SalesmanMap} />
          <PrivateRoute exact path="/reports/orders" component={Order} />
          <PrivateRoute exact path="/pending-orders" component={PendingOrder} />
          <PrivateRoute exact path="/account" component={Account} />
          <PrivateRoute exact path="/admin" component={Admin} />
          <PrivateRoute path="/reports" component={Reports} />
          <PrivateRoute exact path="/ocrPreview" component={OcrPreview} />
          <Redirect from="*" to="/sign-in" />
        </Switch>
      </Suspense>
    </ChunkLoadErrorBoundary>
  );
};

export default Routes;
